import { EntityAdapter } from '@ngrx/entity';
import { createAction, props } from '@ngrx/store';

import { Organization } from '../../models/organization.class';
import { ManageAppsOrganizationEntity, ManageAppsState, Service } from '../states/manage-apps.state';

export enum ManageAppsActionTypes {
    MANAGE_APPS_SET_ASSET_TYPE = '[MANAGE APPS] Set Asset Type',
    MANAGE_APPS_SET_ORGANIZATION_ENTITY = '[MANAGE APPS] Set Organization Entity',
    MANAGE_APPS_SET_STATUS = '[MANAGE APPS] Set Apps Status',
    MANAGE_APPS_SET_PAGE_INDEX = '[MANAGE APPS] Set Apps Page Index',
    MANAGE_APPS_SET_PAGE_SIZE = '[MANAGE APPS] Set Apps Page Size',
    MANAGE_APPS_SET_RESPONSE = '[MANAGE APPS] Set Apps Response',
    MANAGE_APPS_UPDATE_SERVICE = '[MANAGE APPS] Update Service',
    MANAGE_APPS_UPDATE_BRIDGE_NODE = '[MANAGE APPS] Update Bridge Node',
    MANAGE_APPS_SET_SEARCH_TEXT = '[MANAGE APPS] Set Search Text',
    MANAGE_APPS_RESET_SEARCH = '[MANAGE APPS] Reset Search',
    MANAGE_APPS_SET_BRIDGE_ID = '[MANAGE APPS] Set Bridge Id',
    MANAGE_APPS_SET_BRIDGE_NODES = '[MANAGE APPS] Set Bridge Nodes',
    MANAGE_APPS_SET_SELECTED_SERVICE_IDS = '[MANAGE APPS] Set Selected Service Ids',
    MANAGE_APPS_SUBMIT_SELECTED_SERVICE_IDS = '[MANAGE APPS] Submit Selected Service Ids',
    MANAGE_APPS_UPDATE_SERVICES = '[MANAGE APPS] Update Services',
    MANAGE_APPS_GET_ASSIGNABLE_SUBSCRIPTION_IDS = '[MANAGE APPS] Get Assignable Subscription Ids',
    MANAGE_APPS_SET_ASSIGNABLE_SUBSCRIPTION_IDS = '[MANAGE APPS] Set Assignable Subscription Ids',
    MANAGE_APPS_SET_BUNDLE_SUBSCRIPTION_IDS = '[MANAGE APPS] Set Bundle Subscription Ids',
    MANAGE_APPS_SELECT_SERVICES = '[MANAGE APPS] Select Services',
    MANAGE_APPS_CLEAR_SUBSCRIPTIONS_APPROVAL = '[MANAGE APPS] Clear Subscriptions Approval',
    MANAGE_APPS_CLEAR_DATA = '[MANAGE APPS] Clear Data',
    MANAGE_APPS_SET_ORGANIZATION = '[MANAGE APPS] Set Organization',
    MANAGE_APPS_SET_ORGS_APPS = '[MANAGE APPS] Set Organizations and Apps',
    MANAGE_APPS_SET_GET_BY_PAGINATION = '[MANAGE APPS] Set Get By Pagination',
    MANAGE_APPS_SET_COUNT = '[MANAGE APPS] Set Count',
    MANAGE_APPS_SUBMIT_APP_STATUS = '[MANAGE APPS] Submit App Status',
    MANAGE_APPS_UPDATE_PROPERTY = '[MANAGE APPS] Update Property',
    GET_ALL_APPS = '[MANAGE APPS] Get All Apps',
    SET_ALL_APPS = '[MANAGE APPS] Set All Apps',
    GET_APPS_BY_STATUS = '[MANAGE APPS] Get Apps By Status',
    SET_APPS_BY_STATUS = '[MANAGE APPS] Set Apps By Status',
    UPDATE_SUBSCRIBED_SERVICES = '[MANAGE APPS] Update Subscribed Services',
    GET_SUBSCRIPTION_SERVICES_BY_STATUS = '[MANAGE APPS] Get Subscription Services By Status',
    SET_SUBSCRIPTION_APPS_BY_STATUS = '[MANAGE APPS] Set Subscription Apps By Status',
    SET_SUBSCRIPTION_PAGINATION = '[MANAGE APPS] Set Subscription Pagination',
    SET_ORGIDS_AND_SERVICEIDS = '[MANAGE APPS] Set Orgids And Serviceids',
    VALIDATE_AND_SUBSCRIBE_TO_CONNECTOR = '[MANAGE APPS] Validate And Subscribe to Connector',
    VALIDATE_CONNECTOR_SUCCESS_ACTION = '[MANAGE APPS] Validate Connetor Success Action',
    UPDATE_SUBSCRIBED_SERVICES_SUCCESS = '[MANAGE APPS] Update Subscribed Services Success',
    UPDATE_ASSET_DETAILS = '[MANAGE APPS] Update Asset With Description',
    GET_BRIDGE_ASSET_METADATA_ID = '[MANAGE APPS] Get Bride Asset MetadataId',
    SET_BRIDGE_SERVICEID_VS_ASSETID = '[MANAGE APPS] Set Bridge Asset With AssetId',
    SET_SELECTED_BRIDGE_ID = '[MANAGE APPS] Set Selected Bridge ServiceId',
    UPDATE_SERVICES_WITH_BRIDE_NODES = '[MANAGE APPS] Update Services With Bride Nodes',
    SET_SUBSCRIPTIONS_COUNT = '[MANAGE APPS] Set Subscriptions Count',
    SET_SELECTED_PACKAGE_ID = '[MANAGE APPS] Set Selected Package Id',
    UNSUBSCRIBE_FROM_APP = '[MANAGE APPS] Unsubscribe From App',
    UNSUBSCRIBE_FROM_APP_SUCCESS = '[MANAGE APPS] Unsubscribe From App Success',
}

export const ManageAppsSetOrganization = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_ORGANIZATION,
    props<{
        organization: Organization;
    }>()
);

export const ClearManageAppsData = createAction(
    ManageAppsActionTypes.MANAGE_APPS_CLEAR_DATA,
    props<{
        organizationId: string;
    }>()
);

export const ManageAppsSetOrgsAndApps = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_ORGS_APPS,
    props<{
        itemIdArray: string[];
        orgIdArray: string[];
        itemNames: string[];
    }>()
);

export const ManageAppsChangeCategory = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_ASSET_TYPE,
    props<{
        assetType: ManageAppsState['assetType'];
    }>()
);

export const SetManageAppsOrganizationEntity = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_ORGANIZATION_ENTITY,
    props<{
        servicesEntity: ManageAppsOrganizationEntity;
        organizationsAdapter: EntityAdapter<ManageAppsOrganizationEntity>;
    }>()
);

export const SetManageAppsStatus = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_STATUS,
    props<{
        subscriptionStatus: ManageAppsState['subscriptionStatus'];
        organizationId: string;
        forceFetch?: boolean;
    }>()
);

export const SetManageAppsPageSize = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_PAGE_SIZE,
    props<{
        size: number;
    }>()
);

export const SetManageAppsPageIndex = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_PAGE_INDEX,
    props<{
        pageIndex: number;
    }>()
);

export const SetManageAppsSearchText = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_SEARCH_TEXT,
    props<{
        searchText: string;
    }>()
);

export const SetManageAppsResponse = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_RESPONSE,
    props<{
        organizationsAdapter: EntityAdapter<ManageAppsOrganizationEntity>;
        servicesAdapter: EntityAdapter<Service>;
        services: Service[];
        orgIdVsName?: { [property: string]: string };
    }>()
);

export const UpdateManageAppsService = createAction(
    ManageAppsActionTypes.MANAGE_APPS_UPDATE_SERVICE,
    props<{
        organizationsAdapter: EntityAdapter<ManageAppsOrganizationEntity>;
        servicesAdapter: EntityAdapter<Service>;
        assetId: string;
        property: string;
        propertyValue: any;
    }>()
);

export const UpdateManageAppsBridgeNode = createAction(
    ManageAppsActionTypes.MANAGE_APPS_UPDATE_BRIDGE_NODE,
    props<{
        serviceId: string;
        status: string;
        property: string;
        propertyValue: any;
        bridgeNodeId: string;
    }>()
);

export const ManageAppsResetSearchText = createAction(ManageAppsActionTypes.MANAGE_APPS_RESET_SEARCH);

export const ManageAppsSetBridgeId = createAction(ManageAppsActionTypes.MANAGE_APPS_SET_BRIDGE_ID, props<{ assetId: string }>());

export const SubmitManageAppsSelectedServiceIds = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SUBMIT_SELECTED_SERVICE_IDS,
    props<{
        ids: string[];
        orgIds: string[];
    }>()
);

export const ValidateAndSubscribetoConnector = createAction(
    ManageAppsActionTypes.VALIDATE_AND_SUBSCRIBE_TO_CONNECTOR,
    props<{
        resourceName: string;
        orgIds: string[];
        requiredFields?: { [key: string]: string };
    }>()
);
export const ValidateConnetorSuccessAction = createAction(
    ManageAppsActionTypes.VALIDATE_CONNECTOR_SUCCESS_ACTION,
    props<{
        authorizationURI: string;
    }>()
);
export const ManageAppsUpdateMany = createAction(
    ManageAppsActionTypes.MANAGE_APPS_UPDATE_SERVICES,
    props<{
        ids: string[];
        organizationsAdapter: EntityAdapter<ManageAppsOrganizationEntity>;
        servicesAdapter: EntityAdapter<Service>;
        property: string;
        propertyValue: any;
    }>()
);

export const GetManageAppsAssignableSubscriptionIds = createAction(ManageAppsActionTypes.MANAGE_APPS_GET_ASSIGNABLE_SUBSCRIPTION_IDS);

export const SetManageAppsAssignableSubscriptionIds = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_ASSIGNABLE_SUBSCRIPTION_IDS,
    props<{ ids: string[] }>()
);

export const SetManageAppsBundleSubscriptionIds = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_BUNDLE_SUBSCRIPTION_IDS,
    props<{ ids: string[] }>()
);

export const ClearManageAppsSubscriptionApproval = createAction(ManageAppsActionTypes.MANAGE_APPS_CLEAR_SUBSCRIPTIONS_APPROVAL);

export const ManageAppsSetGetByPagination = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_GET_BY_PAGINATION,
    props<{
        status: boolean;
    }>()
);

export const ManageAppsSetCount = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_COUNT,
    props<{
        count: number;
    }>()
);

export const ManageAppsSubmitAppStatus = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SUBMIT_APP_STATUS,
    props<{
        payload: {
            subscriptionIds: string[];
            status: string;
            orgId: string;
        };
    }>()
);

export const ManageAppsUpdateProperty = createAction(
    ManageAppsActionTypes.MANAGE_APPS_UPDATE_PROPERTY,
    props<{
        ids: string[];
        organizationsAdapter: EntityAdapter<ManageAppsOrganizationEntity>;
        servicesAdapter: EntityAdapter<Service>;
        property: string;
        propertyValue: any;
    }>()
);

// export const GetAllApps = createAction(ManageAppsActionTypes.GET_ALL_APPS);
export const GetAllApps = createAction(
    ManageAppsActionTypes.GET_ALL_APPS,
    props<{
        isPackagePage?: boolean;
        considerGlobalRepository?: boolean;
        forceFetch?: boolean;
        allApps?: boolean;
    }>()
);

export const SetAllApps = createAction(
    ManageAppsActionTypes.SET_ALL_APPS,
    props<{
        organizationId: string;
        services: Service[];
        isGlobalRepository?: boolean;
        allApps?: boolean;
    }>()
);

export const GetAppsbyStatus = createAction(ManageAppsActionTypes.GET_APPS_BY_STATUS);

export const SetAppsByStatus = createAction(
    ManageAppsActionTypes.SET_APPS_BY_STATUS,
    props<{
        organizationId: string;
        services: Service[];
        status: string;
    }>()
);

export const GetSubscriptionServicesByStatus = createAction(ManageAppsActionTypes.GET_SUBSCRIPTION_SERVICES_BY_STATUS);

export const SetOrgIdsAndServiceIds = createAction(
    ManageAppsActionTypes.SET_ORGIDS_AND_SERVICEIDS,
    props<{
        orgIdArray?: any[];
        serviceIdArray?: any[];
    }>()
);

export const SetSubscriptionAppsByStatus = createAction(
    ManageAppsActionTypes.SET_SUBSCRIPTION_APPS_BY_STATUS,
    props<{
        organizationId: string;
        services: Service[];
        status: string;
        resetSubscriptions?: boolean;
    }>()
);

export const SetSubscriptionPagination = createAction(
    ManageAppsActionTypes.SET_SUBSCRIPTION_PAGINATION,
    props<{
        pageIndex?: number;
        pageSize?: number;
    }>()
);

export const SetManageAppsSelectedServiceIds = createAction(
    ManageAppsActionTypes.MANAGE_APPS_SET_SELECTED_SERVICE_IDS,
    props<{
        ids: string[];
    }>()
);

export const SelectManageAppsServices = createAction(ManageAppsActionTypes.MANAGE_APPS_SELECT_SERVICES, props<{ ids: string[] }>());

export const UpdateSubscribedServices = createAction(
    ManageAppsActionTypes.UPDATE_SUBSCRIBED_SERVICES,
    props<{
        ids: string[];
    }>()
);

export const UpdateSubscribedServicesSuccess = createAction(ManageAppsActionTypes.UPDATE_SUBSCRIBED_SERVICES_SUCCESS);

export const UpdateAssetDetails = createAction(
    ManageAppsActionTypes.UPDATE_ASSET_DETAILS,
    props<{
        serviceId: string;
        status: string;
        property: string;
        propertyValue: any;
    }>()
);

export const GetBrideAssetMetadataId = createAction(
    ManageAppsActionTypes.GET_BRIDGE_ASSET_METADATA_ID,
    props<{
        serviceId: string;
    }>()
);

export const SetBridgeServiceIdVsAssetId = createAction(
    ManageAppsActionTypes.SET_BRIDGE_SERVICEID_VS_ASSETID,
    props<{ serviceId: string; assetId: string }>()
);

export const SetSelectedBridgeId = createAction(ManageAppsActionTypes.SET_SELECTED_BRIDGE_ID, props<{ serviceId: string }>());

export const UpdateServicesWithBridgeNodes = createAction(
    ManageAppsActionTypes.UPDATE_SERVICES_WITH_BRIDE_NODES,
    props<{ bridgeNodes: any; itemId: string; serviceId: string }>()
);

export const setSubscriptionsCount = createAction(
    ManageAppsActionTypes.SET_SUBSCRIPTIONS_COUNT,
    props<{
        status: string;
        count: number;
    }>()
);

export const SetSelectedPackageId = createAction(ManageAppsActionTypes.SET_SELECTED_PACKAGE_ID, props<{ packageId: string }>());

export const UnsubscribeFromApp = createAction(ManageAppsActionTypes.UNSUBSCRIBE_FROM_APP, props<{ subscriptionId: string }>());

export const UnsubscribeFromAppSuccess = createAction(
    ManageAppsActionTypes.UNSUBSCRIBE_FROM_APP_SUCCESS,
    props<{ subscriptionId: string; message: string }>()
);
